/**
 * The router root. Spec 4.4's file-based tree starts here.
 */
import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, Link, Outlet } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ThemeProvider } from '../features/theme/ThemeProvider';
import { resolveLocale } from '../lib/i18n';

export type RouterContext = {
  readonly queryClient: QueryClient;
};

/* One theme provider over the whole tree, so every layout's `useTheme` reads one preference. */
function RootShell(): ReactNode {
  return (
    <ThemeProvider>
      <Outlet />
    </ThemeProvider>
  );
}

function NotFound(): ReactNode {
  const { t } = useTranslation();
  /*
   * A router `Link`, not `<a href="/">`: a full-page reload out of the SPA is the wrong exit
   * from a 404. The root path is unknown here (this renders outside `/$lang`), so the link
   * resolves the visitor's locale the same way `/` itself does.
   */
  return (
    <div
      className="flex min-h-screen items-center justify-center bg-surface px-4"
      data-testid="not-found"
    >
      <div className="text-center">
        <h1 className="text-lg font-semibold text-ink">{t('states.notFound')}</h1>
        <Link
          to="/$lang"
          params={{ lang: resolveLocale() }}
          className="mt-3 inline-block text-sm text-accent underline"
        >
          {t('states.goHome')}
        </Link>
      </div>
    </div>
  );
}

/**
 * Spec 4.7: a global error boundary "reporting to Sentry with the correlation ID". Sentry
 * arrives with observability; the correlation id is already in the error body, so the
 * surface that shows it exists now.
 */
function ErrorScreen({ error }: { error: Error }): ReactNode {
  return (
    <div
      className="flex min-h-screen items-center justify-center bg-surface px-4"
      data-testid="error-screen"
    >
      <div className="max-w-md text-center">
        <h1 className="text-lg font-semibold text-ink">Something went wrong</h1>
        <p className="mt-2 text-sm text-ink-muted">{error.message}</p>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootShell,
  notFoundComponent: NotFound,
  errorComponent: ErrorScreen,
});
